<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useAuth } from '@/composables/useAuth.ts'
import { useAccess } from '@/composables/useAccess.ts'
import { useUserExtendedData } from '@/composables/useUserExtendedData.ts'
import { useQuery } from '@/composables/api.ts'
import { useSwaggerXData } from '@/composables/useSwagger.ts'
import getEnvironment from '@/utils/env'
import { useCreditorSettings } from '@/composables/useCreditorSettings.ts'
import { isUserToken } from '@/stores/authStore.ts'

import BDialog from '@/components/base/BDialog/BDialog.vue'
import BSelect from '@/components/base/BSelect/BSelect.vue'
import LanguageSelect from './LanguageSelect.vue'
import VismaNetSettingsTab from './VismaNetSettingsTab.vue'

const notificationTypes = ['tickets', 'decisions', 'activities'] as const
type NotificationType = (typeof notificationTypes)[number]

interface EmailNotificationState {
  enabled: boolean
  creditors: string[]
}

type NotificationStateRecord = Record<NotificationType, EmailNotificationState>

const { t } = useI18n()
const route = useRoute()
const isMinimalHeader = computed(() => route.meta.minimalHeader === true)
const { isSolicitor, selectedToken, isSystemUser } = useAuth()
const { canAccessResource } = useAccess()
const environment = getEnvironment()
const userExtendedDataQuery = useUserExtendedData()
const swaggerXDataQuery = useSwaggerXData()

const currentTab = ref<'interface' | 'notifications' | 'visma_net' | 'system'>('interface')

const currentUserId = computed(() => {
  const payload = selectedToken.value?.payload
  return payload && isUserToken(payload) ? payload.u_id : null
})

const userHasEmail = computed(() => {
  const emailData = userExtendedDataQuery.data.value?.email_data
  return emailData?.email != null && emailData.email !== ''
})

const creditorsQuery = useQuery(
  '/creditors',
  { query: { projection: { _id: 1, name: 1 } }, getAll: true },
  { enabled: () => !isSolicitor.value }
)

const creditors = computed(() => creditorsQuery.data.value?._items ?? [])
const userCreditorIds = computed(() => creditors.value.map((c) => c._id))

const {
  isLoading: isLoadingCreditorSettingsFromComposable,
  isPending: isSaving,
  isUserSubscribedToEmail,
  updateUserNotificationSubscription,
  saveCreditorSettingsImmediately,
} = useCreditorSettings()

const isLoadingCreditorSettings = computed(
  () => creditorsQuery.isLoading.value || isLoadingCreditorSettingsFromComposable.value
)

const emailNotifications = computed<NotificationStateRecord>(() => {
  const userId = currentUserId.value
  if (userId == null) {
    return {
      tickets: { enabled: false, creditors: [] },
      decisions: { enabled: false, creditors: [] },
      activities: { enabled: false, creditors: [] },
    }
  }

  const state: NotificationStateRecord = {
    tickets: { enabled: false, creditors: [] },
    decisions: { enabled: false, creditors: [] },
    activities: { enabled: false, creditors: [] },
  }

  for (const notificationType of notificationTypes) {
    const subscribedCreditors: string[] = []
    for (const creditor of creditors.value) {
      if (isUserSubscribedToEmail(creditor._id, notificationType, userId)) {
        subscribedCreditors.push(creditor._id)
      }
    }
    state[notificationType] = {
      enabled: subscribedCreditors.length > 0,
      creditors: subscribedCreditors,
    }
  }

  return state
})

function toggleNotification(notificationType: NotificationType, enabled: boolean) {
  const userId = currentUserId.value
  if (userId == null) return

  for (const creditor of creditors.value) {
    updateUserNotificationSubscription(creditor._id, notificationType, userId, enabled)
  }
}

function updateCreditorSelection(notificationType: NotificationType, creditorIds: string[]) {
  const userId = currentUserId.value
  if (userId == null) return

  for (const creditor of creditors.value) {
    const shouldSubscribe = creditorIds.includes(creditor._id)
    updateUserNotificationSubscription(creditor._id, notificationType, userId, shouldSubscribe)
  }
}

const showNotificationsTab = computed(
  () => !isMinimalHeader.value && !isSolicitor.value && userCreditorIds.value.length > 0
)
const showSystemTab = computed(() => !isMinimalHeader.value && isSystemUser.value)

const canAccessImportFilters = canAccessResource('partner--import-filters')

const vismaNetRegistrationDataQuery = useQuery(
  '/visma-net-registration--data',
  {},
  {
    meta: { notifyOnError: false },
    enabled: computed(() => !isSolicitor.value && !isSystemUser.value && canAccessImportFilters.value),
  }
)
const showVismaNetTab = computed(() => {
  if (isMinimalHeader.value) return false
  const data = vismaNetRegistrationDataQuery.data.value
  return data != null && Array.isArray(data.creditors_data) && data.creditors_data.length > 0
})

const visibleTabs = computed(() => {
  const tabs = [{ key: 'interface', label: 'common.interface' }]
  if (showVismaNetTab.value) {
    tabs.push({ key: 'visma_net', label: 'common.visma_net_import' })
  }
  if (showNotificationsTab.value) {
    tabs.push({ key: 'notifications', label: 'common.notifications' })
  }
  if (showSystemTab.value) {
    tabs.push({ key: 'system', label: 'common.system' })
  }
  return tabs
})

watch(visibleTabs, (tabs) => {
  if (!tabs.some((tab) => tab.key === currentTab.value)) {
    currentTab.value = 'interface'
  }
})

const systemConfigQuery = useQuery(
  '/system--configurations',
  computed(() => ({
    query: {
      where: { name: 'ada' },
      projection: { emails: 1 },
      max_results: 1,
    },
  })),
  { enabled: () => showSystemTab.value }
)

const emailChannels = computed(() => swaggerXDataQuery.data.value?.['x-email-channels'] ?? [])
const configurableChannels = computed(() => emailChannels.value.filter((channel) => channel !== 'no_reply'))
const emailConfiguration = computed(() => systemConfigQuery.data.value?._items?.[0]?.emails ?? {})
const configuredEmailChannels = computed(() => {
  const config = emailConfiguration.value as Record<string, { refresh_token?: string }>
  return Object.keys(config).filter((channel) => config[channel]?.refresh_token != null)
})

defineExpose({
  flushSettings: () => saveCreditorSettingsImmediately(),
})

const showConfirmConfigurationDialog = ref(false)
const chosenEmailChannel = ref<string | null>(null)

function handleConfirmConfigurationDialog(channel: string) {
  showConfirmConfigurationDialog.value = true
  chosenEmailChannel.value = channel
}

function getAuthorizeEmailUrl(channel: string | null): string | null {
  if (channel == null) return null
  const apiRoot = import.meta.env.VITE_APP_API_ROOT
  const appName = import.meta.env.VITE_APP_NAME
  const token = selectedToken.value?.token
  if (token == null) return null

  const url = new URL(
    `authorize_email_channel/init/${encodeURIComponent(channel)}/${encodeURIComponent(appName)}/${encodeURIComponent(token)}`,
    apiRoot
  )
  return url.href
}
</script>

<template>
  <div class="px-4 py-0">
    <v-tabs
      v-model="currentTab"
      variant="standard"
    >
      <v-tab
        v-for="tab in visibleTabs"
        :key="tab.key"
        :value="tab.key"
        class="text-body-medium text-capitalize font-weight-bold text-primary"
      >
        {{ t(tab.label) }}
      </v-tab>
    </v-tabs>

    <v-tabs-window v-model="currentTab">
      <v-tabs-window-item value="interface">
        <language-select />
      </v-tabs-window-item>

      <v-tabs-window-item
        v-if="showVismaNetTab"
        value="visma_net"
      >
        <visma-net-settings-tab :tab-showing="currentTab === 'visma_net'" />
      </v-tabs-window-item>

      <v-tabs-window-item
        v-if="showNotificationsTab"
        value="notifications"
      >
        <div class="py-3">
          <h2 class="text-headline-small">{{ t('common.email') }}</h2>
        </div>

        <v-alert
          v-if="!userHasEmail"
          type="warning"
          variant="tonal"
          class="mb-4"
        >
          <span class="font-weight-bold">{{ t('common.email_address_is_missing') }}</span>
          <div class="mt-2">{{ t('common.email_address_is_missing_notification_description') }}</div>
        </v-alert>

        <div
          v-if="isLoadingCreditorSettings"
          class="d-flex justify-center py-4"
        >
          <v-progress-circular
            indeterminate
            color="primary"
          />
        </div>

        <template v-else>
          <div
            v-for="notificationType in notificationTypes"
            :key="notificationType"
            class="mb-4 mr-1"
          >
            <div class="d-flex justify-space-between align-center">
              <div class="text-body-large font-weight-medium text-medium-emphasis">
                {{ t(`common.${notificationType}`) }}
              </div>
              <v-switch
                :model-value="emailNotifications[notificationType].enabled"
                :disabled="!userHasEmail || isSaving"
                color="brand-primary"
                hide-details
                @update:model-value="toggleNotification(notificationType, Boolean($event))"
              />
            </div>
            <div class="text-body-medium text-medium-emphasis mt-1">
              {{ t(`common.email_notification_${notificationType}_description`) }}
            </div>

            <b-select
              v-if="emailNotifications[notificationType].enabled && creditors.length > 1"
              :model-value="emailNotifications[notificationType].creditors"
              :items="creditors"
              :label="t('common.select_creditors_to_get_email_for')"
              :disabled="!userHasEmail || isSaving"
              item-title="name"
              item-value="_id"
              multiple
              clearable
              class="mt-3"
              @update:model-value="updateCreditorSelection(notificationType, $event as string[])"
            />

            <v-divider class="mt-4" />
          </div>
        </template>
      </v-tabs-window-item>

      <v-tabs-window-item
        v-if="showSystemTab"
        value="system"
      >
        <div class="py-3">
          <h2 class="text-headline-small">{{ t('common.email_channels') }}</h2>
        </div>
        <div class="text-body-medium text-medium-emphasis mb-4">
          {{ t('common.email_channel_description') }}
        </div>

        <div
          v-for="channel in configurableChannels"
          :key="channel"
          class="d-flex justify-space-between align-center py-2"
        >
          <span>{{ t(`title.${channel}`) }}</span>
          <v-btn
            :color="configuredEmailChannels.includes(channel) ? 'primary' : 'error'"
            variant="text"
            @click="handleConfirmConfigurationDialog(channel)"
          >
            {{ configuredEmailChannels.includes(channel) ? t('common.update') : t('common.configure') }}
          </v-btn>
        </div>

        <v-divider class="my-4" />
      </v-tabs-window-item>
    </v-tabs-window>

    <b-dialog
      v-model="showConfirmConfigurationDialog"
      size="medium"
      persistent
    >
      <template #title>{{ t('common.confirm_configuration') }}</template>

      <v-card-text>
        <p>
          {{ t('common.confirm_configuration_info_1') }}
          {{ t('common.confirm_configuration_info_2') }}
          <span class="font-weight-bold">{{ environment?.name }}</span>
        </p>
      </v-card-text>

      <template #footer>
        <v-btn @click="showConfirmConfigurationDialog = false">
          {{ t('common.cancel') }}
        </v-btn>
        <v-btn
          color="primary"
          :href="getAuthorizeEmailUrl(chosenEmailChannel) ?? undefined"
        >
          {{ t('common.ok') }}
        </v-btn>
      </template>
    </b-dialog>
  </div>
</template>
